// Presentational list of org locations. Each row shows compact
// identification (name + code + type chip + city/state) and a small
// action cluster (Edit / Deactivate-or-Reactivate). Clicking Edit
// opens an inline OrgLocationsForm beneath the row; only one row can
// be in edit mode at a time (the hook enforces).
//
// Soft delete only: the toggle flips `is_active`. Deactivated rows
// stay queryable for historical lookups; the section gates whether
// they're visible at all via the "Show deactivated" checkbox.
import type { OrgLocation } from "@/lib/orgLocations/orgLocationsTypes";
import { OrgLocationsForm } from "./OrgLocationsForm";
import type { UseOrgLocationsTab } from "./useOrgLocationsTab";

// Slice 13a: forwarded to OrgLocationsForm for virtual/corporate
// address prefill. Computed by the section; section excludes the
// currently-edited row to prevent self-prefill suggestions.

const ghostBtn =
  "rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700";

const dangerBtn =
  "rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-900/70 dark:bg-zinc-800 dark:text-red-300 dark:hover:bg-red-950/30";

const reactivateBtn =
  "rounded-md border border-emerald-300 bg-white px-2.5 py-1 text-xs text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-emerald-900/70 dark:bg-zinc-800 dark:text-emerald-300 dark:hover:bg-emerald-950/30";

function TypeChip({ type }: { type: string | null }) {
  if (!type) return null;
  const color =
    type === "retail"
      ? "bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-800/50"
      : type === "plant"
      ? "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800/50"
      : type === "corporate"
      ? "bg-violet-50 text-violet-800 border-violet-200 dark:bg-violet-950/40 dark:text-violet-200 dark:border-violet-800/50"
      : type === "virtual"
      ? "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200 dark:bg-fuchsia-950/40 dark:text-fuchsia-200 dark:border-fuchsia-800/50"
      : "bg-gray-50 text-gray-700 border-gray-200 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700";
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium ${color}`}>
      {type}
    </span>
  );
}

function StatusBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-block rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300">
      Active
    </span>
  ) : (
    <span className="inline-block rounded-full border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
      Inactive
    </span>
  );
}

function addressSummary(loc: OrgLocation): string {
  const cityStatePostal = [loc.city, loc.state, loc.postal_code]
    .filter((x): x is string => typeof x === "string" && x.length > 0)
    .join(", ");
  if (loc.address_1) {
    return cityStatePostal ? `${loc.address_1} — ${cityStatePostal}` : loc.address_1;
  }
  return cityStatePostal || "No address yet";
}

type Props = {
  m: UseOrgLocationsTab;
  locations: OrgLocation[];
  /** Slice 14: when false, hides Deactivate/Reactivate buttons. Used by
   *  the trainer-side Location Tools panel (trainers can create+edit
   *  but the soft-delete is admin/owner only). */
  allowDeactivate?: boolean;
  /** Slice 13a: forwarded to OrgLocationsForm for the per-row edit
   *  form. Section already excluded the edited row from this anchor. */
  corporateAnchor?: OrgLocation | null;
};

export function OrgLocationsList({ m, locations, allowDeactivate = true, corporateAnchor }: Props) {
  if (locations.length === 0 && !m.listBusy) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/40 p-6 text-center text-sm text-gray-600 dark:border-zinc-700 dark:bg-zinc-900/30 dark:text-zinc-400">
        No locations yet. Use <span className="font-medium">+ New location</span> to add your first one.
      </div>
    );
  }

  return (
    <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white dark:divide-zinc-800 dark:border-zinc-700 dark:bg-zinc-900/40">
      {locations.map((loc) => {
        const isEditing = m.editingId === loc.id;
        return (
          <li key={loc.id} className="px-3 py-2 space-y-2">
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-zinc-100 truncate">
                    {loc.name || <em className="text-gray-400">(no name)</em>}
                  </span>
                  {loc.location_code ? (
                    <code className="text-[11px] text-gray-500 dark:text-zinc-400">
                      {loc.location_code}
                    </code>
                  ) : null}
                  <TypeChip type={loc.location_type ?? null} />
                  <StatusBadge active={loc.is_active} />
                  {loc.is_public === false ? (
                    <span className="inline-block rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[10px] font-medium text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400">
                      Internal
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-gray-500 dark:text-zinc-400 mt-0.5 truncate">
                  {addressSummary(loc)}
                </div>
                {loc.phone || loc.email ? (
                  <div className="text-xs text-gray-500 dark:text-zinc-500 mt-0.5">
                    {[loc.phone, loc.email].filter(Boolean).join(" · ")}
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {isEditing ? (
                  <button
                    type="button"
                    className={ghostBtn}
                    onClick={m.cancelEdit}
                    disabled={m.actionBusy}
                  >
                    Close
                  </button>
                ) : (
                  <button
                    type="button"
                    className={ghostBtn}
                    onClick={() => m.startEdit(loc)}
                    disabled={m.actionBusy || m.listBusy}
                  >
                    Edit
                  </button>
                )}
                {allowDeactivate ? (
                  loc.is_active ? (
                    <button
                      type="button"
                      className={dangerBtn}
                      onClick={() => void m.toggleActive(loc)}
                      disabled={m.actionBusy || m.listBusy}
                      title="Soft delete — hides from dealer-finder + dropdowns; can be reactivated"
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={reactivateBtn}
                      onClick={() => void m.toggleActive(loc)}
                      disabled={m.actionBusy || m.listBusy}
                    >
                      Reactivate
                    </button>
                  )
                ) : null}
              </div>
            </div>
            {isEditing ? (
              <OrgLocationsForm
                mode="edit"
                form={m.editForm}
                setForm={m.setEditForm}
                onSubmit={m.submitEdit}
                onCancel={m.cancelEdit}
                busy={m.actionBusy}
                corporateAnchor={corporateAnchor}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
